import { InfoIcon } from '@/assets/icons/info-icons/info';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import { useState } from 'react';
import styled from 'styled-components';

interface DeleteAttachmentProps {
  isOpen: boolean;
  entityId: string;
  currentFileName: string;
  onAttachmentDeleted: () => void;
  onClose: () => void;
}

const DeleteAttachment = ({ isOpen, entityId, currentFileName, onAttachmentDeleted, onClose }: DeleteAttachmentProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteAttachmentLoading, setDeleteAttachmentLoading] = useState(false);

  const deleteAttachmentFile = async () => {
    try {
      setDeleteAttachmentLoading(true);
      setErrorMessage(null);

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/legal-entity-attachment/legal-entity/${entityId}/attachments/${currentFileName}`,
        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      onAttachmentDeleted();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setDeleteAttachmentLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Yes, Delete',
        onClick: deleteAttachmentFile,
        disabled: deleteAttachmentLoading
      }}
      showCloseIcon={!deleteAttachmentLoading}
      showCancelButton={!deleteAttachmentLoading}
      isUseOutsideClick={!deleteAttachmentLoading}
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {deleteAttachmentLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Please confirm you want to delete this attachment.</ModalTitle>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner icon={<InfoIcon width={26} height={26} />} title="Error" description={errorMessage} bgColor={themeVar('context.error')} />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>There is no undo for this operation.</ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteAttachment;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
